import { FC, useEffect, useState } from 'react'
import { addDays, format } from 'date-fns'

import EventCard from 'components/EventCard'
import EventDetailModal from 'components/EventDetailModal'
import { ICalendarEvent, ICalendarEventType, ICalendarEventParticipant } from 'interface/calendar'
import { fetchCalendarEvents, fetchCalendarEventParticipants } from 'services/calendar'
import { generateTimeArray } from 'utils/generateTimeArray'
import { groupOverlappingEvents } from 'utils/groupOverlappingEvents'

const HEADER_HEIGHT = 64
const TIME_BLOCK_HEIGHT = 36

interface CalendarDayViewProps {
  officeId: number
  currentDate: Date
  eventTypes: ICalendarEventType[]
  onEventSuccess: (success: string) => void
}

const CalendarDayView: FC<CalendarDayViewProps> = ({
  officeId,
  currentDate,
  eventTypes,
  onEventSuccess,
}) => {
  const timeArrays = generateTimeArray(false)
  const [groupedOverlappingEvents, setGroupedOverlappingEvents] = useState<ICalendarEvent[][]>([])

  const [isEventDetailOpened, setIsEventDetailOpened] = useState(false)
  const [isEventDetailLoading, setIsEventDetailLoading] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(null)
  const [selectedEventParticipants, setSelectedEventParticipants] = useState<ICalendarEventParticipant[]>([])

  useEffect(() => {
    fetchEvents()
    // eslint-disable-next-line
  }, [currentDate, officeId])

  const fetchEvents = () => {
    fetchCalendarEvents({
      office_id: officeId,
      start_date: format(currentDate, 'yyyy-MM-dd'),
      end_date: format(addDays(currentDate, 1), 'yyyy-MM-dd'),
    })
      .then((response) => {
        const mapOfEvents = response.result.events
        const events = mapOfEvents[format(currentDate, 'yyyy-MM-dd')] || []
        events.forEach((event) => {
          event.is_hidden = false
        })
        setGroupedOverlappingEvents(groupOverlappingEvents(events))
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const handleClickCard = (event: ICalendarEvent) => {
    setSelectedEvent(event)
    setIsEventDetailOpened(true)
    setIsEventDetailLoading(true)
    setSelectedEventParticipants([])
    fetchCalendarEventParticipants(event.id)
      .then((response) => {
        setSelectedEventParticipants(response)
        setIsEventDetailLoading(false)
      })
  }

  const handleHideShowEvent = (eventId: number, eventDate: Date, isAll: boolean, isHidden: boolean) => {
    const tempGroupedOverlappingEvents = [...groupedOverlappingEvents]
    for (const tempGroupedOverlappingEvent of tempGroupedOverlappingEvents) {
      for (const event of tempGroupedOverlappingEvent) {
        if (event.id === eventId) {
          event.is_hidden = isHidden
        }
      }
    }
    setGroupedOverlappingEvents(tempGroupedOverlappingEvents)
  }

  return (
    <>
      <div className="flex flex-col flex-auto overflow-hidden">
        <div className="relative flex flex-none overflow-y-scroll">
          <div className="flex-none w-[64px] h-[64px] p-2 border-b border-r border-[#CBCBCB80]"></div>
          <div
            className={'flex flex-col w-full p-2 border-r border-b border-[#E5E7EB]'}
            style={{
              height: HEADER_HEIGHT
            }}
          >
            <div className="text-sm font-normal uppercase text-text-default m-auto">{format(currentDate, 'ccc')}</div>
            <div className="w-[32px] h-[32px] flex items-center justify-center m-auto">
              <div className="text-base font-bold text-text-default">
                {format(currentDate, 'dd')}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-auto overflow-auto rounded-br-[8px] rounded-bl-[8px] border-t border-[#CBCBCB80] bg-white">
          {/** Hours */}
          <div
            className={'relative flex flex-col w-[64px] flex-none'}
          >
            {timeArrays.map((time, timeIndex) => (
              <div
                key={timeIndex}
                className={'flex justify-center text-sm text-text-default font-normal border-r border-[#CBCBCB80] flex-none'}
                style={{
                  height: TIME_BLOCK_HEIGHT * 2
                }}
              >
                {time !== '12 AM' ? (
                  <div className="mt-[-11px]">{time}</div>
                ) : (
                  <div></div>
                )}
              </div>
            ))}
          </div>

          <div
            className={'relative flex flex-1 flex-col'}
            key={format(currentDate, 'MM/dd/yyyy')}
          >
            {[...timeArrays, ...timeArrays].map((_, timeIndex) => (
              <div
                key={timeIndex}
                className={`flex items-center flex-none justify-center text-sm text-text-default font-normal border-r border-[#CBCBCB80] ${timeIndex < [...timeArrays, ...timeArrays].length - 1 ? 'border-b' : ''}`}
                style={{ height: TIME_BLOCK_HEIGHT }}
              />
            ))}
            {groupedOverlappingEvents.map((groupedOverlappingEvent) => {
              return groupedOverlappingEvent.map((event, eventIndex) => {
                if (!event.is_hidden && event.is_canceled === 'false') {
                  return (
                    <EventCard
                      key={event.id}
                      event={event}
                      view="day"
                      groupTotal={groupedOverlappingEvent.length}
                      groupIndex={eventIndex}
                      selectedTypes={eventTypes}
                      onClick={() => handleClickCard(event)}
                    />
                  )
                } else {
                  return <></>
                }
              })
            })}
          </div>
        </div>
      </div>
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          eventDate={currentDate}
          participants={selectedEventParticipants}
          loading={isEventDetailLoading}
          open={isEventDetailOpened}
          onDelete={fetchEvents}
          onHideShow={handleHideShowEvent}
          onSuccess={onEventSuccess}
          onClose={() => setIsEventDetailOpened(false)}
        />
      )}
    </>
  )
}

export default CalendarDayView
